import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Input,
  Badge,
  Button,
  Spinner,
  InputGroup,
  InputGroupText,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import useAwardingList from '../hooks/useAwardingList';
import {
  AWARDING_STATUS_OPTIONS,
  getAwardingStatusConfig,
} from '../utils/awardingHelpers';

const StatusPill = ({ status }) => {
  const { label, color } = getAwardingStatusConfig(status);

  return (
    <Badge
      color={color}
      className='px-2 py-1 fs-12 fw-medium rounded-pill text-nowrap'
      title={label}
    >
      {label}
    </Badge>
  );
};

const AwardingTable = ({
  procurementTypeHeader,
  refreshTrigger = 0,
  endpoint = '/awarding',
  detailBasePath = '/apps-awarding/dtl',
}) => {
  const navigate = useNavigate();

  const {
    data,
    totalRows,
    totalPages,
    currentPage,
    rowsPerPage,
    searchText,
    statusFilter,
    isLoading,
    handleSearch,
    handleStatusFilter,
    handlePageChange,
    handlePerRowsChange,
    handleSort,
    clearFilters,
  } = useAwardingList({ procurementTypeHeader, refreshTrigger, endpoint });

  const goToDetail = id => navigate(`${detailBasePath}/${id}`);

  const isFiltered = Boolean(searchText || statusFilter);

  const MobileCard = ({ item }) => (
    <div className='card mb-3 d-md-none border shadow-none'>
      <div className='card-body p-3'>
        <div className='d-flex justify-content-between align-items-center mb-2'>
          <span className='text-primary fw-semibold fs-13 d-inline-flex align-items-center gap-2'>
            {item.noProc}
            {item.unreadResponseCount > 0 && (
              <Badge color='danger' className='rounded-pill fs-11'>
                {item.unreadResponseCount}
              </Badge>
            )}
          </span>
          <StatusPill status={item.status} />
        </div>

        <h6 className='card-title fw-bold mb-1 text-dark lh-base'>
          {item.title}
        </h6>
        <p className='text-muted fs-12 mb-2'>
          {item.productType?.main} <span className='mx-1'>•</span>{' '}
          {item.productType?.sub}
        </p>

        <div className='mb-3'>
          <span className='text-muted fs-11 text-uppercase d-block mb-1'>
            Proc Type
          </span>
          <span className='fs-12 fw-medium text-dark'>{item.procType}</span>
        </div>

        <Button
          color='light'
          size='sm'
          className='w-100 border bg-white shadow-none text-dark fw-medium py-2'
          onClick={() => goToDetail(item.id)}
        >
          <i className='ri-eye-line align-bottom me-1 text-muted'></i> View
          Detail
        </Button>
      </div>
    </div>
  );

  const columns = [
    {
      id: 'noProc',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>No PROC</span>
      ),
      selector: row => row.noProc,
      cell: row => (
        <span className='d-inline-flex align-items-center gap-2'>
          <span className='text-dark'>{row.noProc}</span>
          {row.unreadResponseCount > 0 && (
            <Badge
              color='danger'
              className='rounded-pill fs-11'
              title={`${row.unreadResponseCount} new response`}
            >
              {row.unreadResponseCount}
            </Badge>
          )}
        </span>
      ),
      width: '150px',
      sortable: true,
    },
    {
      id: 'title',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>Tittle</span>
      ),
      selector: row => row.title,
      cell: row => (
        <span className='text-dark text-truncate' title={row.title}>
          {row.title}
        </span>
      ),
      minWidth: '200px',
      sortable: true,
    },
    {
      id: 'procType',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>
          Proc Type
        </span>
      ),
      selector: row => row.procType,
      cell: row => <span className='text-dark'>{row.procType}</span>,
      width: '180px',
      sortable: true,
    },
    {
      id: 'productType',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>
          Product Type
        </span>
      ),
      selector: row => row.productType?.main,
      cell: row => (
        <span className='text-dark text-truncate'>
          {row.productType?.main}
          {row.productType?.sub && row.productType.sub !== '-'
            ? ` - ${row.productType.sub}`
            : ''}
        </span>
      ),
      minWidth: '200px',
      sortable: false,
    },
    {
      id: 'description',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>
          Description
        </span>
      ),
      selector: row => row.description,
      cell: row => (
        <span className='text-dark text-truncate' title={row.description}>
          {row.description || '-'}
        </span>
      ),
      minWidth: '200px',
      sortable: true,
    },
    {
      id: 'status',
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>Status</span>
      ),
      selector: row => row.status,
      cell: row => <StatusPill status={row.status} />,
      width: '150px',
      sortable: true,
    },
    {
      name: (
        <span className='text-uppercase fw-bold fs-12 text-muted'>Action</span>
      ),
      cell: row => (
        <button
          type='button'
          className='btn btn-soft-primary btn-sm btn-icon'
          onClick={() => goToDetail(row.id)}
          title='View Detail'
        >
          <i className='ri-eye-line align-bottom'></i>
        </button>
      ),
      width: '90px',
      center: true,
      sortable: false,
    },
  ];

  return (
    <div className='pt-1'>
      <Row className='g-2 align-items-center mb-3'>
        <Col xs={12} md={4} lg={3}>
          <InputGroup className='border rounded'>
            <InputGroupText className='bg-white border-0 pe-0'>
              <i className='ri-search-line text-muted'></i>
            </InputGroupText>
            <Input
              type='text'
              placeholder='Search'
              value={searchText}
              onChange={handleSearch}
              className='border-0 shadow-none'
            />
          </InputGroup>
        </Col>
        <Col xs={12} md={4} lg={3}>
          <Input
            type='select'
            value={statusFilter}
            onChange={e => handleStatusFilter(e.target.value)}
            className='form-control'
          >
            <option value=''>All Status</option>
            {AWARDING_STATUS_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Input>
        </Col>
        <Col xs={12} md={4} lg={2}>
          <Button
            color='danger'
            outline
            onClick={clearFilters}
            className='w-100'
          >
            Clear Filter
          </Button>
        </Col>
      </Row>

      <div className='mb-2'>
        <small className='text-muted'>
          {isLoading
            ? 'Loading...'
            : `Showing ${data.items?.length || 0} of ${totalRows} entries${
                isFiltered ? ' (filtered)' : ''
              }`}
        </small>
      </div>

      {isLoading ? (
        <div className='text-center py-5'>
          <Spinner color='primary' />
          <p className='text-muted small mt-2 mb-0'>Loading awarding data...</p>
        </div>
      ) : (
        <>
          <div className='d-md-none mt-3'>
            {data.items?.length > 0 ? (
              data.items.map((item, index) => (
                <MobileCard key={item.id || index} item={item} />
              ))
            ) : (
              <div className='text-center py-4'>
                <i
                  className='ri-inbox-line text-muted'
                  style={{ fontSize: '3rem', opacity: 0.5 }}
                ></i>
                <p className='mt-2 text-muted'>No awarding data found</p>
              </div>
            )}
          </div>

          <div className='d-none d-md-block'>
            <div className='table-responsive'>
              <DataTable
                columns={columns}
                data={data.items}
                pagination={false}
                onSort={handleSort}
                sortServer
                defaultSortFieldId='noProc'
                defaultSortAsc
                highlightOnHover
                responsive
                persistTableHead
                dense
                customStyles={{
                  headCells: {
                    style: {
                      backgroundColor: '#f8f9fa',
                      fontWeight: 'bold',
                      fontSize: '13px',
                      paddingLeft: '12px',
                      paddingRight: '12px',
                    },
                  },
                  cells: {
                    style: {
                      paddingLeft: '12px',
                      paddingRight: '12px',
                      paddingTop: '8px',
                      paddingBottom: '8px',
                      fontSize: '14px',
                    },
                  },
                  rows: { style: { minHeight: '52px' } },
                  table: { style: { width: '100%' } },
                }}
                noDataComponent={
                  <div className='text-center py-5'>
                    <i
                      className='ri-inbox-line text-muted'
                      style={{ fontSize: '3rem', opacity: 0.5 }}
                    ></i>
                    <p className='mt-2 mb-0'>No awarding data found</p>
                  </div>
                }
              />
            </div>
          </div>
        </>
      )}

      <div className='d-flex justify-content-between align-items-center mt-3 px-2 py-2'>
        <div className='d-flex align-items-center'>
          <span className='text-muted me-2'>Rows per page</span>
          <select
            className='form-select form-select-sm'
            style={{ width: 'auto' }}
            value={rowsPerPage}
            onChange={e => handlePerRowsChange(parseInt(e.target.value, 10))}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <div className='d-flex align-items-center'>
          <span className='text-muted me-3'>
            Page {currentPage} of {totalPages}
          </span>
          <div className='btn-group' role='group'>
            <button
              type='button'
              className='btn btn-outline-secondary btn-sm'
              disabled={currentPage <= 1 || isLoading}
              onClick={() => handlePageChange(1)}
            >
              <i className='ri-skip-back-line'></i>
            </button>
            <button
              type='button'
              className='btn btn-outline-secondary btn-sm'
              disabled={currentPage <= 1 || isLoading}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <i className='ri-arrow-left-s-line'></i>
            </button>
            <button
              type='button'
              className='btn btn-outline-secondary btn-sm'
              disabled={currentPage >= totalPages || isLoading}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <i className='ri-arrow-right-s-line'></i>
            </button>
            <button
              type='button'
              className='btn btn-outline-secondary btn-sm'
              disabled={currentPage >= totalPages || isLoading}
              onClick={() => handlePageChange(totalPages)}
            >
              <i className='ri-skip-forward-line'></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(AwardingTable);
